import React from "react";
import { Trash2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { useTableControls, SortableTh, SearchBar, applyControls } from "../tableTools";

const SEARCH_FIELDS = [
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "town", label: "Town" },
  { value: "status", label: "Status" },
];

const getValue = (p, field) => {
  switch (field) {
    case "name": return `${p.first_name || ""} ${p.last_name || ""}`.trim();
    case "email": return p.email;
    case "phone": return p.phone;
    case "town": return p.town;
    case "status": return p.status;
    case "created_at": return p.created_at;
    default: return "";
  }
};

export const ParticipantsTab = (props) => {
  const {
    participants,
    currentAdminEmail,
    handleApprovalCheckboxChange,
    handleToggleAdminRights,
    handleSuspendParticipant,
    handleDeleteParticipant,
    handleResetPassword,
  } = props;
  const ctrl = useTableControls("name", "name");
  const rows = applyControls(participants, ctrl, getValue);
  return (
          <TabsContent value="participants" data-testid="participants-content">
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
              <div className="p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-crew-yellow">CREW REGISTRATION APPROVALS</h2>
                  <p className="text-zinc-400 text-sm mt-1">Review and approve crew member registrations • {participants.length} total</p>
                </div>
                <SearchBar fields={SEARCH_FIELDS} testid="participant-search" {...ctrl} />
              </div>

              {rows.length === 0 ? (
                <div className="p-12 text-center text-zinc-400" data-testid="no-participants">
                  {participants.length === 0 ? "No participant registrations yet." : "No matching crew members."}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs uppercase text-zinc-400 border-b border-zinc-800 bg-zinc-900">
                      <tr>
                        <SortableTh field="name" label="Name" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="email" label="Email" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="phone" label="Phone" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="town" label="Town" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="status" label="Status" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="created_at" label="Registered" {...ctrl} onSort={ctrl.handleSort} />
                        <th className="py-3 px-4 text-center">Approved</th>
                        <th className="py-3 px-4 text-center">Admin</th>
                        <th className="py-3 px-4 text-center">Suspend</th>
                        <th className="py-3 px-4 text-center">Reset PW</th>
                        <th className="py-3 px-4 text-center">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((participant, idx) => (
                        <tr 
                          key={participant.id} 
                          className={`border-b border-zinc-800/50 hover:bg-zinc-900 ${participant.is_admin ? 'bg-crew-yellow/5' : ''}`}
                          data-testid={`participant-${idx}`}
                        >
                          <td className="py-3 px-4">
                            <div className="font-bold text-white flex items-center gap-2">
                              {participant.first_name} {participant.last_name}
                              {participant.is_admin && (
                                <span className="text-xs bg-crew-yellow text-black px-1.5 py-0.5 rounded font-bold">ADMIN</span>
                              )}
                            </div>
                            <div className="text-xs text-zinc-500">
                              "{participant.preferred_name}"
                            </div>
                          </td>
                          <td className="py-3 px-4 text-zinc-300">{participant.email}</td>
                          <td className="py-3 px-4 text-zinc-300 font-mono">{participant.phone}</td>
                          <td className="py-3 px-4 text-zinc-300">{participant.town}</td>
                          <td className="py-3 px-4 text-zinc-300">{participant.status || '—'}</td>
                          <td className="py-3 px-4 text-zinc-400 text-xs">
                            {new Date(participant.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <input
                              type="checkbox"
                              checked={participant.approved || false}
                              onChange={(e) => handleApprovalCheckboxChange(participant, e.target.checked)}
                              className={`w-5 h-5 cursor-pointer ${participant.approved ? 'accent-green-500' : 'accent-yellow-500'}`}
                              style={{
                                accentColor: participant.approved ? '#22c55e' : '#eab308'
                              }}
                              data-testid={`approve-checkbox-${idx}`}
                            />
                          </td>
                          <td className="py-3 px-4 text-center">
                            <input
                              type="checkbox"
                              checked={participant.is_admin || false}
                              onChange={(e) => handleToggleAdminRights(participant.id, participant.email, e.target.checked)}
                              className={`w-5 h-5 cursor-pointer accent-crew-yellow ${participant.email.toLowerCase() === currentAdminEmail ? 'opacity-50' : ''}`}
                              title={
                                participant.email.toLowerCase() === currentAdminEmail
                                  ? "Cannot modify your own admin rights"
                                  : participant.is_admin 
                                    ? "Revoke admin rights" 
                                    : "Grant admin rights"
                              }
                              disabled={participant.email.toLowerCase() === currentAdminEmail && participant.is_admin}
                              data-testid={`admin-checkbox-${idx}`}
                            />
                          </td>
                          <td className="py-3 px-4 text-center">
                            <input
                              type="checkbox"
                              checked={participant.suspended || false}
                              onChange={(e) => handleSuspendParticipant(participant.id, e.target.checked)}
                              className="w-5 h-5 cursor-pointer accent-red-500"
                              data-testid={`suspend-checkbox-${idx}`}
                            />
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              onClick={() => handleResetPassword(participant.id, `${participant.first_name} ${participant.last_name}`)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-500 hover:bg-blue-500 hover:text-white p-1 h-8 w-8"
                              title="Reset Password"
                              data-testid={`reset-pw-button-${idx}`}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              onClick={() => handleDeleteParticipant(participant.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:bg-red-500 hover:text-white p-1 h-8 w-8"
                              data-testid={`delete-button-${idx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
  );
};
